import axios from 'axios';
import { logger } from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.98.59:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Handle FormData - let browser set Content-Type automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      logger.error('Authentication failed - token expired or invalid');
      // Token expired or invalid - only redirect if not already on login page
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        logger.warn('Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
      }
    }
    logger.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Coupon interfaces
export interface Coupon {
  id: number;
  code: string;
  name: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  minimum_amount?: number;
  maximum_discount?: number;
  usage_limit?: number;
  used_count: number;
  is_active: boolean;
  starts_at?: string;
  expires_at?: string;
  description?: string;
  computed_status?: string;
  computed_discount_display?: string;
  created_at: string;
  updated_at: string;
}

export type CouponFormData = Omit<Coupon, 'id' | 'used_count' | 'computed_status' | 'computed_discount_display' | 'created_at' | 'updated_at'>;

export interface CouponFilters {
  search?: string;
  status?: string;
  discount_type?: string;
}

export interface CouponStats {
  total: number;
  active: number;
  expired: number;
  used: number;
}

export interface CouponValidation {
  valid: boolean;
  coupon?: {
    id: number;
    code: string;
    name: string;
    discount_type: string;
    discount_value: number;
    discount_display: string;
  };
  discount_amount?: number;
  message: string;
}

// Order interfaces
export interface Order {
  id: number;
  order_number: string;
  user_id: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  subtotal: number;
  tax_amount: number;
  shipping_amount: number;
  total_amount: number;
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_method: 'manual' | 'card' | 'bank_transfer';
  payment_screenshot?: string;
  payment_notes?: string;
  shipping_address: any;
  billing_address: any;
  notes?: string;
  shipped_at?: string;
  delivered_at?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
    phone?: string;
  };
  order_items?: OrderItem[];
}

export interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  quantity: number;
  price: number;
  total: number;
  product_snapshot?: any;
  product?: {
    id: number;
    name: string;
    product_code?: string;
    image?: string;
  };
}

export interface OrderFilters {
  search?: string;
  status?: string;
  payment_status?: string;
  payment_method?: string;
  date_from?: string;
  date_to?: string;
}

export interface OrderStatistics {
  total_orders: number;
  pending_orders: number;
  processing_orders: number;
  shipped_orders: number;
  delivered_orders: number;
  cancelled_orders: number;
  pending_payments: number;
  paid_orders: number;
  total_revenue: number;
  this_month_orders: number;
  this_month_revenue: number;
  this_week_orders: number;
}

export interface ExchangeRequestData {
  order_number: string;
  date_of_purchase: string;
  customer_name: string;
  email_address: string;
  phone_number: string;
  product_name: string;
  product_size: string;
  product_color: string;
  reason_for_exchange: string;
  reason_details?: string;
  replacement_name: string;
  replacement_size: string;
  replacement_color: string;
  photo?: File;
  additional_comments?: string;
  status?: string;
  admin_notes?: string;
}

export interface ExchangeRequest extends Omit<ExchangeRequestData, 'photo'> {
  id: number;
  photo_path?: string;
  processed_by?: number;
  processed_at?: string;
  created_at: string;
  updated_at: string;
  processed_by_user?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface ExchangeRequestFilters {
  status?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
  per_page?: number;
  page?: number;
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Hierarchy support
  parent_id?: number | null;
  parent?: Category;
  children?: Category[];
  full_path?: string;
  level?: number;
  is_parent?: boolean;
}

export interface Brand {
  id: number;
  name: string;
  slug: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Address {
  id: number;
  user_id: number;
  name: string;
  phone: string;
  company?: string;
  address_line_1: string;
  address_line_2?: string;
  township: string;
  state_region: string;
  postal_code?: string;
  country: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}


export interface ProductSize {
  name: string;
  stock_quantity: number;
  reserved_quantity: number;
  price?: number;
  compare_at_price?: number;
  weight?: number;
  barcode?: string;
  is_active: boolean;
  track_inventory: boolean;
}

export interface ProductVariant {
  id: number;
  product_id: number;
  sku: string;
  color: string;
  color_code?: string;
  sizes: ProductSize[];
  price?: number;
  compare_at_price?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: number;
  product_code: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  compare_at_price?: number;
  category: string | Category; // Can be string (legacy) or Category object
  category_id?: number;
  brand_id?: number;
  product_images: string[]; // Array of image URLs, first one is primary
  hover_image?: string;
  // Relationships (when included)
  brand?: Brand;
  variants?: ProductVariant[];
  in_stock: number;
  low_stock_threshold?: number;
  features: string[];
  is_active: boolean;
  is_featured: boolean;
  is_best_seller?: boolean;
  is_trending?: boolean;
  show_on_website?: boolean;
  meta_title?: string;
  meta_description?: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  // Promotion-related fields
  discounted_price?: number;
  discount_percentage?: number;
  has_promotion?: boolean;
  active_promotions?: any[];
  promotional_price?: number;
  promotional_price_set_at?: string;
  applied_promotion_id?: number;
}

export interface ProductsResponse {
  data: Product[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export const apiService = {
  // Get all products with optional filters
  getProducts: async (params?: {
    category?: string;
    search?: string;
    page?: number;
  }): Promise<ProductsResponse> => {
    const response = await api.get('/products', { params });
    return response.data;
  },

  // Get a single product by ID
  getProduct: async (id: number): Promise<Product> => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  // Get a single product by slug
  getProductBySlug: async (slug: string): Promise<Product> => {
    const response = await api.get(`/products/slug/${slug}`);
    return response.data;
  },

  // Get featured products
  getFeaturedProducts: async (): Promise<Product[]> => {
    const response = await api.get('/products/featured/list');
    return response.data;
  },

  // Get best seller products
  getBestSellerProducts: async (): Promise<Product[]> => {
    const response = await api.get('/products/best-sellers/list');
    return response.data;
  },

  // Get trending products
  getTrendingProducts: async (): Promise<Product[]> => {
    const response = await api.get('/products/trending/list');
    return response.data;
  },

  // Get product categories (legacy - returns strings)
  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/products/categories/list');
    return response.data;
  },

  // Get all categories with full data
  getCategoriesData: async (): Promise<Category[]> => {
    const response = await api.get('/categories');
    return response.data;
  },

  // Get all brands
  getBrands: async (): Promise<Brand[]> => {
    const response = await api.get('/brands', { 
      params: { 
        per_page: 100, 
        _t: Date.now() 
      },
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data.data || response.data;
  },


  // Get active categories
  getActiveCategories: async (): Promise<Category[]> => {
    const response = await api.get('/categories/active');
    return response.data;
  },

  // Get active brands
  getActiveBrands: async (): Promise<Brand[]> => {
    const response = await api.get('/brands/active');
    return response.data;
  },

  // Create a new product (admin functionality)
  createProduct: async (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<Product> => {
    const response = await api.post('/products', product);
    return response.data;
  },

  // Update a product (admin functionality)
  updateProduct: async (id: number, product: Partial<Product>): Promise<Product> => {
    const response = await api.put(`/products/${id}`, product);
    return response.data;
  },

  // Delete a product (admin functionality)
  deleteProduct: async (id: number): Promise<void> => {
    await api.delete(`/products/${id}`);
  },

  // Authentication methods
  login: async (credentials: { email: string; password: string }) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone?: string;
    date_of_birth?: string;
  }) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Keep user-specific cart data for when they log back in
    // This matches popular ecommerce behavior where cart persists
    
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/user');
    return response.data;
  },

  updateProfile: async (profileData: {
    name?: string;
    email?: string;
    phone?: string;
    date_of_birth?: string;
  }) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },

  changePassword: async (passwordData: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }) => {
    const response = await api.put('/auth/password', passwordData);
    return response.data;
  },

  // Image upload methods
  uploadSingleImage: async (file: File, type: 'primary' | 'hover' | 'additional' = 'primary') => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);
    
    const response = await api.post('/admin/products/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadMultipleImages: async (files: File[]) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('images[]', file);
    });
    
    const response = await api.post('/admin/products/upload-multiple-images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Profile Management
  getProfile: async () => {
    const response = await api.get('/profile');
    return response.data;
  },

  updateCustomerProfile: async (profileData: any) => {
    const response = await api.put('/profile', profileData);
    return response.data;
  },

  updatePassword: async (passwordData: any) => {
    const response = await api.put('/profile/password', passwordData);
    return response.data;
  },

  uploadAvatar: async (avatarFile: File) => {
    const formData = new FormData();
    formData.append('avatar', avatarFile);
    
    const response = await api.post('/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Address Management
  getAddresses: async (): Promise<Address[]> => {
    const response = await api.get('/addresses');
    return response.data;
  },

  createAddress: async (addressData: Omit<Address, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<Address> => {
    const response = await api.post('/addresses', addressData);
    return response.data;
  },

  updateAddress: async (id: number, addressData: Partial<Address>): Promise<Address> => {
    const response = await api.put(`/addresses/${id}`, addressData);
    return response.data;
  },

  deleteAddress: async (id: number): Promise<void> => {
    await api.delete(`/addresses/${id}`);
  },

  // Calculate delivery fee for an address
  calculateDeliveryFee: async (addressId: number): Promise<{
    delivery_fee: number;
    estimated_days: number;
    township_delivery_id: number;
    formatted_price: string;
  } | null> => {
    try {
      const response = await api.get(`/addresses/${addressId}/delivery-fee`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.status === 404) {
        throw new Error('Delivery not available for this location');
      } else if (error.response?.status === 400) {
        throw new Error('Township and state/region are required for delivery fee calculation');
      }
      throw new Error('Failed to calculate delivery fee');
    }
  },

  setDefaultAddress: async (id: number): Promise<Address> => {
    const response = await api.put(`/addresses/${id}/set-default`);
    return response.data;
  },

  deleteAccount: async (deleteData: any) => {
    const response = await api.delete('/profile/account', { data: deleteData });
    return response.data;
  },

  // Admin VIP Management
  toggleCustomerVipStatus: async (customerId: number) => {
    const response = await api.put(`/admin/customers/${customerId}/toggle-vip`);
    return response.data;
  },

  bulkUpdateVipStatus: async (customerIds: number[], isVip: boolean) => {
    const response = await api.post('/admin/customers/bulk-vip-update', {
      customer_ids: customerIds,
      is_vip: isVip
    });
    return response.data;
  },

  // Order Management
  getOrders: async (params?: OrderFilters) => {
    const response = await api.get('/admin/orders', { params });
    return response.data;
  },

  getOrder: async (id: number) => {
    const response = await api.get(`/admin/orders/${id}`);
    return response.data;
  },

  updateOrderStatus: async (id: number, data: { status?: string; payment_status?: string; notes?: string }) => {
    const response = await api.put(`/admin/orders/${id}`, data);
    return response.data;
  },

  approvePayment: async (id: number) => {
    const response = await api.put(`/admin/orders/${id}/approve-payment`);
    return response.data;
  },

  rejectPayment: async (id: number, reason: string) => {
    const response = await api.put(`/admin/orders/${id}/reject-payment`, { reason });
    return response.data;
  },

  getPendingPayments: async () => {
    const response = await api.get('/admin/payments/pending');
    return response.data;
  },

  getOrderStatistics: async (): Promise<OrderStatistics> => {
    const response = await api.get('/admin/orders/statistics');
    return response.data;
  },

  // Create a manual (social-media) order
  createManualOrder: async (data: {
    customer_name: string;
    customer_phone: string;
    customer_email?: string;
    customer_address?: string;
    items: Array<{ variant_id: number; size: string; quantity: number }>;
    delivery_fee?: number;
    discount_amount?: number;
    payment_method?: string;
    payment_status?: 'pending' | 'paid';
    status?: 'pending' | 'processing' | 'shipped' | 'delivered';
    notes?: string;
  }) => {
    const response = await api.post('/admin/orders', data);
    return response.data;
  },

  // Update a manual order's items (adjusts stock automatically)
  updateManualOrderItems: async (
    orderId: number,
    data: {
      items: Array<{ variant_id: number; size: string; quantity: number }>;
      delivery_fee?: number;
      discount_amount?: number;
    }
  ) => {
    const response = await api.put(`/admin/orders/${orderId}/items`, data);
    return response.data;
  },

  // Exchange Request Management
  submitExchangeRequest: async (data: ExchangeRequestData | FormData) => {
    const response = await api.post('/exchange-requests', data);
    return response.data;
  },

  // Admin Exchange Request Management
  getAdminExchangeRequests: async (params?: {
    page?: number;
    search?: string;
    status?: string;
    per_page?: number;
  }) => {
    const response = await api.get('/admin/exchange-requests', { params });
    return response.data;
  },

  getExchangeRequestStatistics: async () => {
    const response = await api.get('/admin/exchange-requests/statistics');
    return response.data;
  },

  getExchangeRequestById: async (id: number) => {
    const response = await api.get(`/admin/exchange-requests/${id}`);
    return response.data;
  },

  updateExchangeRequestStatus: async (id: number, data: {
    status: string;
    admin_notes?: string;
  }) => {
    const response = await api.put(`/admin/exchange-requests/${id}/status`, data);
    return response.data;
  },

  getExchangeRequests: async (params?: ExchangeRequestFilters) => {
    const response = await api.get('/admin/exchange-requests', { params });
    return response.data;
  },

  getExchangeRequest: async (id: number) => {
    const response = await api.get(`/admin/exchange-requests/${id}`);
    return response.data;
  },

  updateExchangeRequest: async (id: number, data: Partial<ExchangeRequestData>) => {
    const response = await api.put(`/admin/exchange-requests/${id}`, data);
    return response.data;
  },

  deleteExchangeRequest: async (id: number) => {
    const response = await api.delete(`/admin/exchange-requests/${id}`);
    return response.data;
  },

  // Coupon Management
  getCoupons: async (filters?: CouponFilters): Promise<Coupon[]> => {
    const response = await api.get('/admin/coupons', { params: filters });
    return response.data;
  },

  getCouponStats: async (): Promise<CouponStats> => {
    const response = await api.get('/admin/coupons/statistics');
    return response.data;
  },

  getCoupon: async (id: number): Promise<Coupon> => {
    const response = await api.get(`/admin/coupons/${id}`);
    return response.data;
  },

  createCoupon: async (data: Omit<Coupon, 'id' | 'used_count' | 'computed_status' | 'computed_discount_display' | 'created_at' | 'updated_at'>): Promise<Coupon> => {
    const response = await api.post('/admin/coupons', data);
    return response.data.coupon;
  },

  updateCoupon: async (id: number, data: Partial<Coupon>): Promise<Coupon> => {
    const response = await api.put(`/admin/coupons/${id}`, data);
    return response.data.coupon;
  },

  deleteCoupon: async (id: number): Promise<void> => {
    await api.delete(`/admin/coupons/${id}`);
  },

  validateCoupon: async (code: string, amount: number): Promise<CouponValidation> => {
    const response = await api.post('/coupons/validate', { code, amount });
    return response.data;
  },

  // Cart Stock Management
  reserveStock: async (items: Array<{variant_id: number, size_name: string, quantity: number}>) => {
    const response = await api.post('/cart/reserve-stock', { items });
    return response.data;
  },

  releaseStock: async (items: Array<{variant_id: number, size_name: string, quantity: number}>) => {
    const response = await api.post('/cart/release-stock', { items });
    return response.data;
  },

  updateReservedStock: async (variant_id: number, size_name: string, old_quantity: number, new_quantity: number) => {
    const response = await api.put('/cart/update-reserved-stock', {
      variant_id,
      size_name,
      old_quantity,
      new_quantity
    });
    return response.data;
  },

  checkStockAvailability: async (items: Array<{variant_id: number, size_name: string}>) => {
    const response = await api.post('/cart/check-stock', { items });
    return response.data;
  },

  // User Orders
  getUserOrders: async (params?: {status?: string, payment_status?: string, per_page?: number}) => {
    const response = await api.get('/orders', { params });
    return response.data;
  },

  getRecentOrders: async () => {
    const response = await api.get('/orders/recent');
    return response.data;
  },

  getUserOrder: async (id: number) => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },

  getUserOrderByNumber: async (orderNumber: string) => {
    const response = await api.get(`/orders/by-number/${orderNumber}`);
    return response.data;
  },

  getOrderStatus: async (orderNumber: string) => {
    const response = await api.get(`/orders/status/${orderNumber}`);
    return response.data;
  },

  // Generic API methods for flexible usage
  get: async (url: string, config?: any) => {
    try {
      const response = await api.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  post: async (url: string, data?: any) => {
    const response = await api.post(url, data);
    return response;
  },

  put: async (url: string, data?: any) => {
    const response = await api.put(url, data);
    return response?.data || response || { success: false, message: 'No response received' };
  },

  delete: async (url: string) => {
    const response = await api.delete(url);
    return response?.data || response || { success: false, message: 'No response received' };
  },
};

export default apiService;
